import React, { useState } from 'react';
import { CheckCircle, Award, BookOpen, Heart, Play } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const About = () => {
  const [videoPlaying, setVideoPlaying] = useState(false);

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Header />

      <main className="flex-grow">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-sky-50 to-white">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-gray-900">
              About <span className="text-sky-600">Elyte Fitness</span>
            </h1>
            <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
              Where science meets fitness. We combine physical therapy expertise with personalized coaching to help you achieve your dream body.
            </p>
          </div>
        </section>

        {/* Founder Section */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
                A Letter From Our <span className="text-sky-600">Founder</span>
              </h2>
            </div>

            <div className="flex flex-col md:flex-row gap-12 items-center">
              {/* Founder image */}
              <div className="w-full md:w-2/5">
                <div className="relative">
                  <div className="absolute inset-0 bg-sky-200 rounded-full opacity-20 transform scale-110 blur-lg"></div>
                  <img
                    src="/founder_img.avif"
                    alt="Dr. Olu Olapinsin"
                    className="relative z-10 rounded-3xl shadow-lg w-full"
                  />
                </div>
              </div>

              {/* Founder story */}
              <div className="w-full md:w-3/5">
                <h3 className="text-2xl font-bold mb-4 text-sky-600">Why I Started This Company...</h3>
                
                <div className="text-gray-700 space-y-4">
                  <p>
                    Before I got into helping other people with their fitness goals, I myself struggled on my own fitness journey. I didn't like the way my body looked, and I noticed that people who had great physiques seemed to be happier and more confident.
                  </p>
                  
                  <p>
                    Wanting to make a positive change in my life, I began my journey into the world of health and fitness. Ever since that point in my life, I became obsessed with the world of health and fitness.
                  </p>
                  
                  <p>
                    Fast forward 7 years later, I became a Doctor of Physical Therapy to help people recover from injuries and achieve their highest level of function. Still having an intense passion for helping people to look their best, I also obtained my certification as a Personal Trainer and Strength and Conditioning Coach.
                  </p>
                </div>
                
                <div className="mt-8 bg-sky-50 p-6 rounded-xl shadow-sm">
                  <h3 className="text-xl font-bold mb-3 text-sky-600">My Mission</h3>
                  <p className="text-gray-700">
                    Since starting this business, I have made it my life mission to help people look and feel their best. My company combines my expertise as a Doctor of Physical Therapy and as a Certified Strength and Conditioning Coach. My unique approach to helping you see success is what I believe separates me from other fitness experts in the market through helping people to develop the correct Strategy for Training, Nutrition and Mindset. These 3 elements are what help my clients to succeed.
                  </p>
                </div>
              </div>
            </div>

       
          </div>
        </section>

        {/* What Makes Us Different Section */}
        <section className="py-16 bg-sky-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
                What Makes Us <span className="text-sky-600">Different</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our science-based approach combines physical therapy expertise with personalized fitness coaching
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {/* Feature 1 */}
              <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition duration-300">
                <div className="text-sky-600 mb-4">
                  <CheckCircle size={40} />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Individually Customized Plans</h3>
                <p className="text-gray-700">
                  Hyper-targeted training and nutrition plans designed specifically for your unique fitness goals and body type
                </p>
              </div>

              {/* Feature 2 */}
              <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition duration-300">
                <div className="text-sky-600 mb-4">
                  <CheckCircle size={40} />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Guaranteed Results</h3>
                <p className="text-gray-700">
                  We stick with you until you reach your fitness goals, providing ongoing support and adjustments to ensure success
                </p>
              </div>

              {/* Feature 3 */}
              <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition duration-300">
                <div className="text-sky-600 mb-4">
                  <CheckCircle size={40} />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Flexible Agreements</h3>
                <p className="text-gray-700">
                  We provide options without lengthy commitments, allowing you to choose what works best for your lifestyle and needs
                </p>
              </div>
            </div>

            <div className="bg-sky-600 rounded-xl p-8 text-white">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold mb-4">What We Avoid</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1 text-sky-200">✕</div>
                      <p>Generic Plans - One-size-fits-all programs that don't target your specific needs</p>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1 text-sky-200">✕</div>
                      <p>No Guarantee - Most people start a program and end it without any results</p>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1 text-sky-200">✕</div>
                      <p>Long-Term Contracts - Limited flexibility to work with your needs and situation</p>
                    </li>
                  </ul>
                </div>
                <div className="flex justify-center">
                  <a
                    href="/#packages"
                    className="bg-white text-sky-600 font-bold text-lg px-8 py-4 rounded-xl hover:bg-gray-100 transition duration-300 shadow-lg"
                  >
                    View Pricing Options
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Meet Your Coach Section */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
                Meet Your <span className="text-sky-600">Coach</span>
              </h2>
            </div>

            <div className="flex flex-col md:flex-row gap-12 items-start">
              {/* Coach Image/Video */}
              <div className="w-full md:w-2/5">
                <div className="relative">
                  {!videoPlaying ? (
                    <>
                      <div className="absolute inset-0 bg-sky-200 rounded-3xl opacity-20 transform scale-110 blur-lg"></div>
                      <img
                        src="https://img.youtube.com/vi/paCsgpgBRbk/maxresdefault.jpg"
                        alt="Dr. Olu Olapinsin"
                        className="relative z-10 rounded-3xl shadow-lg w-full object-cover"
                      />
                      {/* Play button overlay */}
                      <button 
                        onClick={() => setVideoPlaying(true)}
                        className="absolute inset-0 z-20 flex items-center justify-center bg-black bg-opacity-30 rounded-3xl transition-opacity hover:bg-opacity-40"
                      >
                        <div className="w-16 h-16 bg-sky-600 rounded-full flex items-center justify-center">
                          <Play size={32} className="text-white ml-1" />
                        </div>
                        <span className="absolute bottom-6 text-white font-medium bg-black bg-opacity-50 px-4 py-2 rounded-full">
                          Watch Video Introduction
                        </span>
                      </button>
                    </>
                  ) : (
                    <div className="rounded-3xl overflow-hidden shadow-lg relative pt-[56.25%] w-full">
                      <iframe 
                        className="absolute top-0 left-0 w-full h-full"
                        src="https://www.youtube.com/embed/paCsgpgBRbk?autoplay=1"
                        title="Dr. Olu Olapinsin Introduction"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  )}
                </div>
                
                {/* Video toggle button when video is playing */}
                {videoPlaying && (
                  <button 
                    onClick={() => setVideoPlaying(false)}
                    className="mt-4 text-sky-600 font-medium flex items-center justify-center gap-2 w-full hover:text-sky-700"
                  >
                    Show Image
                  </button>
                )}
              </div>

              {/* Coach Details */}
              <div className="w-full md:w-3/5">
                <h3 className="text-2xl font-bold mb-4 text-sky-600">Dr. Olu Olapinsin</h3>
                <p className="text-xl text-gray-700 mb-8">
                  Coach - Founder of Elyte Fitness
                </p>

                <div className="space-y-6 mb-8">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1 text-sky-600">
                      <Award size={24} />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">Fitness Coach for over 15 years</p>
                      <p className="text-gray-700">Extensive experience helping clients reach their fitness goals</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1 text-sky-600">
                      <BookOpen size={24} />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">Doctor of Physical Therapy for 10 years</p>
                      <p className="text-gray-700">Professional medical expertise in physical rehabilitation and injury prevention</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1 text-sky-600">
                      <Award size={24} />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">Certified Strength and Conditioning Specialist for 10 years</p>
                      <p className="text-gray-700">Specialized training in optimizing physical performance and strength</p>
                    </div>
                  </div>
                </div>

                <div className="bg-sky-50 p-6 rounded-xl shadow-sm mb-8">
                  <h4 className="font-bold text-lg mb-4 text-sky-600">I love...</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <Heart className="text-sky-600" size={16} />
                      <span className="text-gray-700">Hanging out with family & friends</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Heart className="text-sky-600" size={16} />
                      <span className="text-gray-700">Working out</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Heart className="text-sky-600" size={16} />
                      <span className="text-gray-700">Hikes</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Heart className="text-sky-600" size={16} />
                      <span className="text-gray-700">Motivating others</span>
                    </div>
                  </div>
                </div>

                <div className="bg-sky-600 p-6 rounded-xl text-white">
                  <p className="italic text-lg">
                    "I'm a Certified Strength and Conditioning Coach who can help you achieve the body of your dreams... in the shortest time period possible."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About; 